import { styled, keyframes } from "styled-components";

const moveGradient = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

export const HowWeWorkStyled = styled.section`
  position: relative;
  overflow: hidden;
  background: var(--bg-secondary);

  .howWeWorkContainer {
    max-width: 1400px;
    width: 100%;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 1fr;
    min-height: 700px;

    .contentSide {
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 80px 60px;
      position: relative;
      z-index: 2;

      .sectionLabel {
        display: inline-flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 24px;

        .line {
          width: 40px;
          height: 3px;
          background: var(--accent);
          border-radius: 2px;
        }

        span {
          font-family: Poppins;
          font-size: 14px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 3px;
          color: var(--accent);
        }
      }

      h1 {
        font-family: Poppins;
        font-size: 64px;
        font-weight: 800;
        line-height: 1.1;
        color: var(--text-primary);
        margin-bottom: 24px;

        .outline {
          -webkit-text-stroke: 2px var(--text-primary);
          -webkit-text-fill-color: transparent;
        }
      }

      p {
        font-family: Poppins;
        font-size: 20px;
        line-height: 1.7;
        color: var(--text-secondary);
        margin-bottom: 40px;
        max-width: 450px;
      }

      .ctaWrapper {
        display: flex;
        gap: 20px;
        flex-wrap: wrap;

        a {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          padding: 18px 36px;
          font-family: Poppins;
          font-size: 16px;
          font-weight: 600;
          text-decoration: none;
          border-radius: 14px;
          transition: all 0.3s ease;

          &.primaryBtn {
            background: linear-gradient(135deg, var(--accent) 0%, #c12f2a 100%);
            color: #fff;
            box-shadow: 0 10px 30px rgba(217, 58, 53, 0.3);

            &:hover {
              transform: translateY(-3px);
              box-shadow: 0 15px 40px rgba(217, 58, 53, 0.4);
            }
          }

          &.secondaryBtn {
            background: var(--card-bg);
            color: var(--text-primary);
            border: 2px solid var(--card-border);

            &:hover {
              border-color: var(--primary);
            }
          }

          svg {
            width: 20px;
            height: 20px;
            transition: transform 0.3s ease;
          }

          &:hover svg {
            transform: translateX(5px);
          }
        }
      }

      .statsRow {
        display: flex;
        gap: 50px;
        margin-top: 60px;
        padding-top: 40px;
        border-top: 1px solid var(--card-border);

        .statItem {
          .number {
            font-family: Poppins;
            font-size: 48px;
            font-weight: 800;
            color: var(--primary);
            line-height: 1;
            display: flex;
            align-items: baseline;
            gap: 4px;

            span {
              font-size: 32px;
              color: var(--accent);
            }
          }

          .label {
            font-family: Poppins;
            font-size: 14px;
            font-weight: 500;
            color: var(--text-secondary);
            margin-top: 8px;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
        }
      }
    }

    .imageSide {
      position: relative;
      background: linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%);
      background-size: 200% 200%;
      animation: ${moveGradient} 10s ease infinite;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;

      &::before {
        content: "";
        position: absolute;
        inset: 0;
        background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
        opacity: 0.5;
      }

      .imageWrapper {
        position: relative;
        width: 80%;
        max-width: 500px;

        img {
          width: 100%;
          height: auto;
          border-radius: 24px;
          box-shadow: 0 30px 80px rgba(0, 0, 0, 0.3);
        }
      }

      .floatingCard {
        position: absolute;
        background: var(--card-bg);
        border-radius: 16px;
        padding: 20px 24px;
        box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
        display: flex;
        align-items: center;
        gap: 16px;

        &.card1 {
          bottom: 10%;
          left: -20px;
        }

        &.card2 {
          top: 10%;
          right: -20px;
        }

        .cardIcon {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, var(--accent) 0%, var(--primary) 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;

          svg {
            width: 24px;
            height: 24px;
            color: #fff;
          }
        }

        .cardText {
          .value {
            font-family: Poppins;
            font-size: 24px;
            font-weight: 700;
            color: var(--text-primary);
          }

          .label {
            font-family: Poppins;
            font-size: 12px;
            color: var(--text-secondary);
          }
        }
      }
    }
  }

  @media (max-width: 1024px) {
    .howWeWorkContainer {
      grid-template-columns: 1fr;

      .contentSide {
        padding: 60px 30px;
        text-align: center;
        align-items: center;

        h1 {
          font-size: 48px;
        }

        p {
          margin-left: auto;
          margin-right: auto;
        }

        .ctaWrapper {
          justify-content: center;
        }

        .statsRow {
          justify-content: center;
        }
      }

      .imageSide {
        min-height: 400px;
      }
    }
  }

  @media (max-width: 640px) {
    .howWeWorkContainer .contentSide {
      padding: 40px 20px;

      h1 {
        font-size: 36px;
      }

      p {
        font-size: 16px;
      }

      .ctaWrapper {
        flex-direction: column;

        a {
          width: 100%;
          justify-content: center;
        }
      }

      .statsRow {
        flex-direction: column;
        gap: 30px;
        align-items: center;
      }
    }
  }
`;
